import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import type { Theme } from '@mui/material';
import { Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { useFieldById } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';

const CropMonitoringReportRecommendations = ({ fieldId, appInstance }: { fieldId: number; appInstance: any }) => {
    const navigate = useNavigate();

    const fieldQuery = useFieldById({ id: fieldId });

    const followingCampaignsCards: {
        title: string;
        description: string;
        btnText: string;
        btnClick: () => void;
        enabled: boolean;
    }[] = [
        {
            title: _('Planificá tu próximo barbecho'),
            description: _('Encontrá la mejor opción de barbecho según el lote y el cultivo que se viene.'),
            btnText: _('Planificar barbecho'),
            enabled: appInstance.enableCropProtectionRecommendation,
            btnClick: () => {
                mpTrackEvent('Crop Protection Recommendation began', {
                    entry_source: 'crop-monitoring-report',
                    crop: 'corn',
                    cp_type: 'weed',
                });
                navigate(
                    reverse('cropProtectionRecommendation', {
                        qs: {
                            fieldId: fieldId,
                            crop: 'corn',
                            userNodeId: fieldQuery.data?.userNode,
                        },
                    })
                );
            },
        },
        {
            title: _('Descubrí la mejor rotación para tu lote'),
            description: _('Probá combinaciones de cultivos para cuidar la tierra y potenciar los resultados.'),
            btnText: _('Ir al simulador de rotación'),
            enabled: appInstance.enableCarbonCalculator,
            btnClick: () => navigate(reverse('CarbonCalculator', { params: { fieldId: fieldId } })),
        },
    ].filter((card) => card.enabled);

    if (!followingCampaignsCards.length) return null;

    return (
        <>
            <Typography variant='h5' fontWeight='semibold'>
                {_('Planificá tu próxima campaña')}
            </Typography>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent='space-between'
                alignItems='stretch'
                flexWrap='nowrap'
            >
                {followingCampaignsCards.map((card) => (
                    <Stack
                        key={card.title}
                        direction='column'
                        spacing={2}
                        justifyContent='space-between'
                        alignItems='start'
                        sx={{
                            p: 2,
                            border: (t: Theme) => `1px solid ${t.palette.divider}`,
                            backgroundColor: '#f5f5f5',
                            width: { xs: '100%', md: '50%' },
                        }}
                    >
                        <Typography variant='h6' color={'primary'} fontWeight='semibold'>
                            {card.title}
                        </Typography>
                        <Typography variant='body1' color={'primary.mui.inactive'}>
                            {card.description}
                        </Typography>
                        <Button
                            onClick={card.btnClick}
                            variant='contained'
                            color='secondary'
                            endIcon={<ArrowForwardIosIcon sx={{ fontSize: '12px !important' }} />}
                        >
                            {card.btnText}
                        </Button>
                    </Stack>
                ))}
            </Stack>
        </>
    );
};

export default CropMonitoringReportRecommendations;
